import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconTriangle, IconTriangleInverted } from '@tabler/icons-react'
import { isEmpty, startsWith } from 'lodash'
import { sendFullStoryEvent } from '@/utils/fullStoryHelpers'
import { formatDuration, getSentimentDisplay } from '@/utils/helpers'
import { EChart } from '@/components/charts/EChart'
import { fetchSentimentForCallId } from '@/reducers/media/media.actions'
import { sentimentColors } from '@/utils/constants'

import './SentimentGraph.scss'

export const SentimentGraph = ({
  callId,
  progress,
  duration,
  metadata,
  setProgress,
  organizationId,
}) => {
  const anyDuration = duration || metadata?.call_duration
  const dispatch = useDispatch()
  const { sentiment, loading } = useSelector((state) => state.media)

  const handleClick = ({ event, data }, chart) => {
    const pointInPixel = [event.offsetX, event.offsetY]
    const pointInGrid = chart.convertFromPixel('grid', pointInPixel)
    const seriesData = chart.getOption().series[0].data

    let closestPoint = null
    let minDistance = Infinity

    seriesData.forEach((item) => {
      const dataX = item[0]
      const distance = Math.abs(dataX - pointInGrid[0])

      if (distance < minDistance) {
        minDistance = distance
        closestPoint = item
      }
    })

    if (closestPoint) {
      const xValue = closestPoint[0]
      setProgress(xValue)
    }

    if (!isEmpty(data)) {
      const [timestamp, sentiment] = data
      if (timestamp && sentiment) {
        const blockStartTime = formatDuration(timestamp)
        const formattedTimestamp = startsWith(blockStartTime, '00:')
          ? blockStartTime.slice(3)
          : blockStartTime

        sendFullStoryEvent('Sentiment clicked', {
          timestamp: formattedTimestamp,
          sentiment: getSentimentDisplay(sentiment),
        })
      }
    }
  }

  const option = useMemo(
    () => ({
      backgroundColor: 'transparent',
      tooltip: {
        show: true,
        trigger: 'axis',
        axisPointer: { type: 'line', axis: 'x' },
        backgroundColor: 'var(--color-background-inverse)',
        borderColor: 'var(--color-background-inverse)',
        borderRadius: 16,
        borderWidth: '1',
        textStyle: {
          color: 'var(--color-text-on-fill)',
        },
        formatter: (params) => {
          if (isEmpty(sentiment.data)) {
            return null
          }

          const point = params[0]
          // eslint-disable-next-line no-unused-vars
          const [timestamp, sentimentScore] = point.value

          if (!timestamp) {
            return null
          }

          const blockStartTime = formatDuration(timestamp)
          const formattedTimestamp = startsWith(blockStartTime, '00:')
            ? blockStartTime.slice(3)
            : blockStartTime

          return `
          <strong>Timestamp:</strong> <span>${formattedTimestamp}</span><br />
          <strong>Sentiment:</strong> <span>${point.marker} ${getSentimentDisplay(sentimentScore)}</span>
          `
        },
      },
      grid: { left: 0, right: 0, top: 0, bottom: 0 },
      xAxis: {
        name: 'Time',
        type: 'value',
        min: 0,
        max: anyDuration,
        show: false,
        boundaryGap: false, // make sure there's no gap so it can be 1:1 with the progress slider
      },
      yAxis: {
        name: 'Sentiment',
        type: 'value',
        min: 1,
        max: 9,
        show: false,
      },
      visualMap: {
        type: 'continuous',
        show: false,
        min: 1,
        max: 9,
        dimension: 1, // 0 = x, 1 = y
        seriesIndex: 0,
        inRange: { color: sentimentColors },
      },
      series: [
        {
          type: 'line',
          name: 'Sentiment',
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 3 },
          emphasis: { disabled: true },
          data: sentiment.data,
          zlevel: 1,
          z: 10,
          triggerEvent: true,
        },
      ],
    }),
    [sentiment.data.length, anyDuration]
  )

  useEffect(() => {
    if (callId) {
      dispatch(fetchSentimentForCallId(callId, organizationId))
    }
  }, [callId])

  if (!anyDuration || isEmpty(sentiment.data)) {
    return null
  }

  return (
    <div className="sentiment-graph-container" data-testid="sentiment-graph-container">
      <div className="sentiment-graph__positive">
        <IconTriangle />
        Positive
      </div>
      <div className="sentiment-graph__negative">
        <IconTriangleInverted />
        Negative
      </div>
      {!loading.sentiment && (
        <EChart
          option={option}
          style={{ height: '60px' }}
          progress={progress}
          duration={anyDuration}
          events={{ click: handleClick }}
        />
      )}
    </div>
  )
}
